import { useFormContext } from "react-hook-form";

import type { InputT } from "@patient-watch/types";
import { ChoiceCard, ChoiceGroup } from "@patient-watch/forms";
import { FormField, FormItem, FormMessage } from "@patient-watch/ui/form";
import { useId } from "react";
import AddOption from "./AddOption";

interface Props {
    input: InputT;
}

function normalizeOptions(input: InputT): {
    value: string;
    label: string;
    description?: string | null;
    segments?: { label: string; description?: string | null }[];
    disabled?: boolean;
}[] {
    if (input.scale_options?.length) {
        return input.scale_options.map((o) => ({
            value: String(o.value),
            label: o.label,
            description: o.description,
            segments: o.segments ?? undefined,
            // A dictionary option marked is_disabled is shown but not selectable.
            disabled: (o as { disabled?: boolean }).disabled === true,
        }));
    }
    return (input.options ?? []).map((o) => ({ value: o, label: o }));
}

const MultipleChoice = ({ input }: Props) => {
    const { control } = useFormContext();
    const options = normalizeOptions(input);
    const labelId = `${useId()}-label`;
    const questionLabel = input.input_label?.trim() ?? "";
    const hasQuestionLabel = questionLabel.length > 0;

    return (
        <FormField
            control={control}
            name={input.input_id}
            render={({ field, fieldState }) => (
                <FormItem>
                    <ChoiceCard
                        labelId={labelId}
                        caption={input.input_caption}
                        label={input.input_label}
                        required={!input.is_optional}
                        description={input.input_description}
                        invalid={fieldState.invalid}
                    >
                        <ChoiceGroup
                            name={input.input_id}
                            multiple
                            options={options.map((o) => ({
                                value: o.value,
                                label: o.label,
                                description: o.description,
                                segments: o.segments,
                                disabled: o.disabled,
                            }))}
                            value={Array.isArray(field.value) ? field.value : []}
                            onChange={(next) => field.onChange(next)}
                            invalid={fieldState.invalid}
                            labelledBy={hasQuestionLabel ? labelId : undefined}
                            data-testid={`form-options-${input.input_id}`}
                        />
                        <FormMessage />
                        <AddOption input={input} />
                    </ChoiceCard>
                </FormItem>
            )}
        />
    );
};

export default MultipleChoice;
